import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, Building2, FileText, FolderLock, LogOut, Plus, ShieldCheck, Trash2, TrendingUp, Upload, Users,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import Seo from "@/components/Seo";
import AdminStats from "@/components/AdminStats";
import { useLang } from "@/i18n";

const inputCls =
  "w-full rounded-lg border border-white/15 bg-navy-900 px-4 py-3 text-sm text-slate-200 outline-none transition-colors placeholder:text-slate-600 focus:border-gold";

const STATUS_LABELS = { nuova: "Nuova", in_lavorazione: "In lavorazione", gestita: "Gestita" };

const fmtSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
};

function LoginCard() {
  const { login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success(t("Accesso effettuato"), { description: t("Benvenuti nel portale riservato.") });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 pb-28 pt-44">
      <Reveal>
        <div className="card-surface rounded-2xl p-8 sm:p-10" data-testid="login-card">
          <div className="flex flex-col items-center text-center">
            <LogoMark size={52} />
            <h1 className="mt-6 font-serif text-2xl text-slate-100">{t("Area Clienti Riservata")}</h1>
            <p className="mt-2 text-sm font-light text-slate-400">
              {t("Accesso al portale documentale e allo stato delle operazioni.")}
            </p>
          </div>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className={inputCls}
              data-testid="login-email-input"
            />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputCls}
              data-testid="login-password-input"
            />
            {error && (
              <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full rounded-full bg-gold py-3.5 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-light disabled:opacity-50"
            >
              {busy ? t("Accesso in corso…") : t("Accedi al Portale")}
            </button>
          </form>
          <div className="mt-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("oppure")}</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>
          <button
            type="button"
            data-testid="google-login-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/area-riservata";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="mt-6 flex w-full items-center justify-center gap-3 rounded-full border border-white/20 py-3.5 text-sm text-slate-200 transition-colors hover:border-gold/60 hover:text-gold"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 2.09 14.96 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.04 12 5.04z"/>
              <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58l3.68 2.85c2.15-1.99 3.75-4.92 3.75-8.67z"/>
              <path fill="#FBBC05" d="M5.84 14.09a7.19 7.19 0 0 1 0-4.18L2.18 7.07a11 11 0 0 0 0 9.86l3.66-2.84z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.68-2.85c-.98.66-2.23 1.06-3.6 1.06-2.86 0-5.29-2.27-6.16-4.95l-3.66 2.84C3.99 20.53 7.7 23 12 23z"/>
            </svg>
            {t("Continua con Google")}
          </button>
        </div>
      </Reveal>
    </div>
  );
}

async function downloadFile(doc, t) {
  try {
    const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.original_filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(t("Download non riuscito"), { description: formatApiError(err) });
  }
}

const OPZ = {
  it: [
    { name: "Acquisizione Target Alpha", phase: "Negoziazione SPA", pct: 68 },
    { name: "Ristrutturazione finanziaria", phase: "Attestazione piano", pct: 82 },
    { name: "Costituzione holding estera", phase: "Ruling in corso", pct: 35 },
  ],
  en: [
    { name: "Target Alpha Acquisition", phase: "SPA negotiation", pct: 68 },
    { name: "Financial restructuring", phase: "Plan certification", pct: 82 },
    { name: "Foreign holding formation", phase: "Ruling in progress", pct: 35 },
  ],
};

function ClientDashboard({ user, logout }) {
  const [docs, setDocs] = useState(null);
  const { lang, t } = useLang();

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-36 sm:px-8" data-testid="client-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">{t("Portale Cliente")}</p>
          <h1 className="mt-3 font-serif text-3xl text-slate-100">
            {lang === "en" ? `Welcome, ${user.name}` : `Benvenuto, ${user.name}`}
          </h1>
          <p className="mt-1 text-sm text-slate-400">{user.company}</p>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-300 transition-colors hover:border-gold hover:text-gold"
        >
          <LogOut size={15} /> {t("Esci")}
        </button>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="card-surface h-full rounded-2xl p-8" data-testid="client-docs-panel">
            <div className="flex items-center gap-3">
              <FolderLock className="text-gold" size={22} />
              <h2 className="font-serif text-xl text-slate-100">{t("Documenti Riservati")}</h2>
            </div>
            {docs === null ? (
              <p className="mt-6 font-mono text-sm text-slate-500">{t("Caricamento…")}</p>
            ) : docs.length === 0 ? (
              <p className="mt-6 rounded-xl border border-white/10 bg-navy-900/60 px-5 py-6 text-sm font-light text-slate-400" data-testid="client-docs-empty">
                {t("Nessun documento disponibile al momento. I documenti condivisi dallo studio appariranno qui.")}
              </p>
            ) : (
              <ul className="mt-6 space-y-3">
                {docs.map((d, i) => (
                  <li key={d.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-navy-900/60 px-5 py-4" data-testid={`client-doc-${i}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      <FileText size={17} className="shrink-0 text-gold" />
                      <div className="min-w-0">
                        <p className="truncate text-sm text-slate-200">{d.original_filename}</p>
                        <p className="mt-0.5 font-mono text-[11px] text-slate-500">
                          {new Date(d.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "it-IT")} · {fmtSize(d.size)}
                          {d.note ? ` · ${d.note}` : ""}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadFile(d, t)}
                      data-testid={`client-doc-download-${i}`}
                      className="shrink-0 rounded-full border border-gold/40 px-4 py-1.5 text-xs text-gold transition-colors hover:bg-gold/10"
                    >
                      {t("Scarica")}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="card-surface h-full rounded-2xl p-8" data-testid="client-ops-panel">
            <div className="flex items-center gap-3">
              <TrendingUp className="text-gold" size={22} />
              <h2 className="font-serif text-xl text-slate-100">{t("Stato delle Operazioni")}</h2>
            </div>
            <div className="mt-6 space-y-6">
              {OPZ[lang].map((o) => (
                <div key={o.name}>
                  <div className="flex items-center justify-between text-sm">
                    <p className="text-slate-200">{o.name}</p>
                    <p className="font-mono text-xs text-gold">{o.pct}%</p>
                  </div>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{o.phase}</p>
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-gradient-to-r from-gold-light via-gold to-gold-dark" style={{ width: `${o.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-start gap-3 rounded-xl border border-gold/20 bg-gold/5 p-4 text-xs font-light leading-relaxed text-slate-400">
              <ShieldCheck size={16} className="mt-0.5 shrink-0 text-gold" />
              {t("Tutti i documenti sono trattati secondo i protocolli di riservatezza dello studio. Per nuovi incarichi contattate il vostro partner di riferimento.")}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}

function AdminDashboard({ user, logout }) {
  const [tab, setTab] = useState("statistiche");
  const [requests, setRequests] = useState([]);
  const [insights, setInsights] = useState([]);
  const [clients, setClients] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState({ title: "", topic: "Fisco", excerpt: "", content: "" });
  const [clientForm, setClientForm] = useState({ name: "", email: "", password: "", company: "" });
  const [docClient, setDocClient] = useState("");
  const [docNote, setDocNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadAll = () => {
    api.get("/contact").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/insights").then((r) => setInsights(r.data)).catch(() => {});
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/documents").then((r) => setDocuments(r.data)).catch(() => {});
  };

  useEffect(loadAll, []);

  const clientName = (id) => {
    const c = clients.find((x) => x.id === id);
    return c ? `${c.name}${c.company ? ` — ${c.company}` : ""}` : "Cliente";
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/contact/${id}`, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const publishInsight = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insights", form);
      toast.success("Articolo pubblicato");
      setForm({ title: "", topic: "Fisco", excerpt: "", content: "" });
      api.get("/insights").then((r) => setInsights(r.data));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteInsight = async (id) => {
    try {
      await api.delete(`/insights/${id}`);
      setInsights((prev) => prev.filter((a) => a.id !== id));
      toast.success("Articolo eliminato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createClient = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/clients", clientForm);
      toast.success("Account cliente creato");
      setClientForm({ name: "", email: "", password: "", company: "" });
      api.get("/admin/clients").then((r) => setClients(r.data));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadDoc = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!docClient) {
      toast.error("Selezionate un cliente");
      return;
    }
    if (!file) {
      toast.error("Selezionate un file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("client_id", docClient);
      if (docNote) fd.append("note", docNote);
      fd.append("file", file);
      await api.post("/admin/documents", fd);
      toast.success("Documento caricato e assegnato al cliente");
      setDocNote("");
      if (fileRef.current) fileRef.current.value = "";
      api.get("/admin/documents").then((r) => setDocuments(r.data));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (id) => {
    try {
      await api.delete(`/admin/documents/${id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      toast.success("Documento eliminato");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const tabs = [
    { id: "statistiche", label: "Statistiche", icon: BarChart3 },
    { id: "richieste", label: "Richieste di Contatto", icon: FileText },
    { id: "insights", label: "Gestione Insights", icon: Building2 },
    { id: "clienti", label: "Account Clienti", icon: Users },
    { id: "documenti", label: "Documenti Clienti", icon: FolderLock },
  ];

  return (
    <div className="mx-auto max-w-7xl px-5 pb-28 pt-36 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Portale dello Studio</p>
          <h1 className="mt-3 font-serif text-3xl text-slate-100">{user.name}</h1>
          <p className="mt-1 text-sm text-slate-400">Pannello amministrativo</p>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-slate-300 transition-colors hover:border-gold hover:text-gold"
        >
          <LogOut size={15} /> Esci
        </button>
      </div>

      <div className="mt-10 flex flex-wrap gap-3" data-testid="admin-tabs">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            data-testid={`admin-tab-${tb.id}`}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm transition-all ${
              tab === tb.id ? "border-gold bg-gold font-semibold text-navy-950" : "border-white/15 text-slate-300 hover:border-gold/50 hover:text-gold"
            }`}
          >
            <tb.icon size={15} /> {tb.label}
          </button>
        ))}
      </div>

      {tab === "statistiche" && (
        <div className="mt-8" data-testid="admin-stats-tab">
          <AdminStats />
        </div>
      )}

      {tab === "richieste" && (
        <div className="mt-8 space-y-4" data-testid="admin-requests-panel">
          {requests.length === 0 && (
            <p className="card-surface rounded-2xl p-10 text-center text-sm text-slate-400">Nessuna richiesta ricevuta finora.</p>
          )}
          {requests.map((r, i) => (
            <div key={r.id} className="card-surface rounded-2xl p-6 sm:p-7" data-testid={`admin-request-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-base font-medium text-slate-100">{r.nome} {r.azienda ? <span className="text-slate-400">— {r.azienda}</span> : null}</p>
                  <p className="mt-1 font-mono text-xs text-slate-500">
                    {r.email} {r.telefono ? `· ${r.telefono}` : ""} · {new Date(r.created_at).toLocaleDateString("it-IT")}
                  </p>
                  <span className="mt-3 inline-block rounded-full border border-gold/30 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] text-gold">
                    {r.tipo_servizio}
                  </span>
                  <p className="mt-4 max-w-3xl text-sm font-light leading-relaxed text-slate-300">{r.messaggio}</p>
                </div>
                <select
                  value={r.status}
                  onChange={(e) => setStatus(r.id, e.target.value)}
                  data-testid={`admin-request-status-${i}`}
                  className="rounded-lg border border-white/15 bg-navy-900 px-3 py-2 text-xs text-slate-200 outline-none focus:border-gold"
                >
                  {Object.entries(STATUS_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "insights" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="admin-insights-panel">
          <form onSubmit={publishInsight} className="card-surface space-y-4 rounded-2xl p-7" data-testid="admin-insight-form">
            <h2 className="flex items-center gap-2 font-serif text-xl text-slate-100"><Plus size={18} className="text-gold" /> Nuovo Articolo</h2>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titolo" className={inputCls} data-testid="insight-title-input" />
            <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className={inputCls} data-testid="insight-topic-select">
              {["Fisco", "M&A", "Governance", "Giurisprudenza"].map((tp) => <option key={tp}>{tp}</option>)}
            </select>
            <textarea required rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Abstract" className={inputCls} data-testid="insight-excerpt-input" />
            <textarea required rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Testo integrale (paragrafi separati da riga vuota)" className={inputCls} data-testid="insight-content-input" />
            <button type="submit" data-testid="insight-publish-btn" className="rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-light">
              Pubblica
            </button>
          </form>
          <div className="card-surface rounded-2xl p-7" data-testid="admin-insights-list">
            <h2 className="font-serif text-xl text-slate-100">Articoli Pubblicati ({insights.length})</h2>
            <ul className="mt-5 space-y-3">
              {insights.map((a, i) => (
                <li key={a.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-navy-900/60 px-4 py-3" data-testid={`admin-insight-row-${i}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm text-slate-200">{a.title}</p>
                    <p className="font-mono text-[11px] text-slate-500">{a.topic}</p>
                  </div>
                  <button onClick={() => deleteInsight(a.id)} data-testid={`admin-insight-delete-${i}`} className="shrink-0 rounded-lg border border-red-500/30 p-2 text-red-400 transition-colors hover:bg-red-500/10" aria-label="Elimina">
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === "clienti" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="admin-clients-panel">
          <form onSubmit={createClient} className="card-surface space-y-4 rounded-2xl p-7" data-testid="admin-client-form">
            <h2 className="flex items-center gap-2 font-serif text-xl text-slate-100"><Plus size={18} className="text-gold" /> Nuovo Account Cliente</h2>
            <input required value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} placeholder="Nome e cognome" className={inputCls} data-testid="client-name-input" />
            <input required type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="client-email-input" />
            <input value={clientForm.company} onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })} placeholder="Azienda" className={inputCls} data-testid="client-company-input" />
            <input required type="password" minLength={6} value={clientForm.password} onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })} placeholder="Password temporanea" className={inputCls} data-testid="client-password-input" />
            <button type="submit" data-testid="client-create-btn" className="rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-light">
              Crea Account
            </button>
          </form>
          <div className="card-surface rounded-2xl p-7" data-testid="admin-clients-list">
            <h2 className="font-serif text-xl text-slate-100">Clienti Registrati ({clients.length})</h2>
            <ul className="mt-5 space-y-3">
              {clients.map((cl, i) => (
                <li key={cl.id} className="rounded-xl border border-white/10 bg-navy-900/60 px-4 py-3" data-testid={`admin-client-row-${i}`}>
                  <p className="text-sm text-slate-200">{cl.name}</p>
                  <p className="font-mono text-[11px] text-slate-500">{cl.email} {cl.company ? `· ${cl.company}` : ""}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === "documenti" && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="admin-documents-panel">
          <form onSubmit={uploadDoc} className="card-surface space-y-4 rounded-2xl p-7" data-testid="admin-document-form">
            <h2 className="flex items-center gap-2 font-serif text-xl text-slate-100"><Upload size={18} className="text-gold" /> Carica Documento Riservato</h2>
            <div>
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Cliente destinatario</label>
              <select required value={docClient} onChange={(e) => setDocClient(e.target.value)} className={inputCls} data-testid="document-client-select">
                <option value="">Seleziona il cliente…</option>
                {clients.map((cl) => (
                  <option key={cl.id} value={cl.id}>{cl.name}{cl.company ? ` — ${cl.company}` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">File (max 25 MB)</label>
              <input
                ref={fileRef}
                type="file"
                required
                data-testid="document-file-input"
                className="w-full rounded-lg border border-dashed border-white/20 bg-navy-900 px-4 py-6 text-sm text-slate-400 file:mr-4 file:rounded-full file:border-0 file:bg-gold file:px-4 file:py-2 file:text-xs file:font-semibold file:text-navy-950 hover:border-gold/40"
              />
            </div>
            <input value={docNote} onChange={(e) => setDocNote(e.target.value)} placeholder="Nota per il cliente (facoltativa)" className={inputCls} data-testid="document-note-input" />
            <button type="submit" disabled={uploading} data-testid="document-upload-btn" className="rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-light disabled:opacity-50">
              {uploading ? "Caricamento in corso…" : "Carica e Assegna"}
            </button>
          </form>
          <div className="card-surface rounded-2xl p-7" data-testid="admin-documents-list">
            <h2 className="font-serif text-xl text-slate-100">Documenti Condivisi ({documents.length})</h2>
            <ul className="mt-5 space-y-3">
              {documents.length === 0 && (
                <li className="rounded-xl border border-white/10 bg-navy-900/60 px-4 py-6 text-center text-sm text-slate-500" data-testid="admin-documents-empty">
                  Nessun documento caricato.
                </li>
              )}
              {documents.map((d, i) => (
                <li key={d.id} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-navy-900/60 px-4 py-3" data-testid={`admin-doc-row-${i}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText size={16} className="shrink-0 text-gold" />
                    <div className="min-w-0">
                      <p className="truncate text-sm text-slate-200">{d.original_filename}</p>
                      <p className="font-mono text-[11px] text-slate-500">
                        {clientName(d.client_id)} · {fmtSize(d.size)} · {new Date(d.created_at).toLocaleDateString("it-IT")}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button onClick={() => downloadFile(d, (s) => s)} data-testid={`admin-doc-download-${i}`} className="rounded-lg border border-gold/40 px-3 py-2 text-xs text-gold transition-colors hover:bg-gold/10">
                      Scarica
                    </button>
                    <button onClick={() => deleteDoc(d.id)} data-testid={`admin-doc-delete-${i}`} className="rounded-lg border border-red-500/30 p-2 text-red-400 transition-colors hover:bg-red-500/10" aria-label="Elimina">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AreaRiservata() {
  const { user, logout } = useAuth();
  const { t } = useLang();

  if (user === null) {
    return (
      <main className="flex min-h-screen items-center justify-center" data-testid="area-riservata-loading">
        <p className="font-mono text-sm text-slate-500">{t("Verifica della sessione…")}</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen navy-mesh" data-testid="area-riservata-page">
      <Seo title="Area Riservata Clienti | Mediolanum Consulting" noindex />
      {!user ? (
        <LoginCard />
      ) : user.role === "admin" ? (
        <AdminDashboard user={user} logout={logout} />
      ) : (
        <ClientDashboard user={user} logout={logout} />
      )}
    </main>
  );
}
